import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Avatar from '@/components/Avatar';
import { 
  Camera, Mail, User, Building2, Briefcase, Edit2, 
  LogOut, ChevronRight, Loader2, X, Trash2, ImagePlus, CheckCircle, Home
} from 'lucide-react';
import { toast } from 'sonner';
import { MobileMenuTrigger } from '@/components/Layout';

// Format Lacs
const formatLacs = (value) => {
  if (!value && value !== 0) return '₹0 L';
  return `₹${parseFloat(value).toFixed(2)} L`;
};

const Profile = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bioModalOpen, setBioModalOpen] = useState(false);
  const [bio, setBio] = useState(user?.bio || '');
  const [savingBio, setSavingBio] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.getProfileStats();
      setStats(response.data || {});
    } catch (error) {
      console.error('Failed to load stats');
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File must be less than 5MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, HEIC, WebP images are allowed');
      return;
    }

    setUploading(true);
    setPhotoModalOpen(false);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.uploadProfilePhoto(formData);
      toast.success('Profile photo updated');
      
      // Reload the page to get updated user data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    setPhotoModalOpen(false);
    setUploading(true);
    
    try {
      await api.removeProfilePhoto();
      toast.success('Profile photo removed');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to remove photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveBio = async () => {
    setSavingBio(true);
    try {
      await api.updateProfileBio(bio.trim());
      toast.success('Bio updated');
      setBioModalOpen(false);
    } catch (error) {
      toast.error('Failed to update bio');
    } finally {
      setSavingBio(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('auth_token');
      if (onLogout) onLogout();
      navigate('/login');
    }
  };

  if (!user) return null;

  const displayName = user.full_name || user.rep_name || 'User';
  const cityTeam = [user.city, user.team].filter(Boolean).join(' | ') || '—';

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-24">
      {/* Header Section */}
      <div className="bg-gradient-to-br from-[#111827] to-[#1F2937] text-white px-4 pt-4 pb-12">
        {/* Navigation Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 -ml-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="home-btn"
          >
            <Home size={20} className="text-white" />
          </button>
          <h2 className="text-sm font-medium text-white/80">Profile</h2>
          <MobileMenuTrigger user={user} className="text-white hover:bg-white/10" />
        </div>
        
        <div className="flex flex-col items-center">
          {/* Avatar with camera overlay */}
          <div className="relative mb-4">
            {uploading ? (
              <div className="w-[100px] h-[100px] rounded-full bg-slate-700 flex items-center justify-center">
                <Loader2 size={32} className="animate-spin text-white" />
              </div>
            ) : (
              <Avatar
                src={user.profile_image_url}
                name={displayName}
                size="profile"
                showBorder
                onClick={() => setPhotoModalOpen(true)}
              />
            )}
            
            {/* Camera icon overlay */}
            <button
              onClick={() => setPhotoModalOpen(true)}
              className="absolute bottom-0 right-0 w-8 h-8 bg-[#5B3DF5] rounded-full flex items-center justify-center shadow-lg border-2 border-white hover:bg-[#4A2FE0] transition-colors"
            >
              <Camera size={16} className="text-white" />
            </button>
          </div>
          
          {/* Name */}
          <h1 className="text-xl font-bold text-center">{displayName}</h1>
          
          {/* City | Team */}
          <p className="text-sm text-slate-300 mt-1">{cityTeam}</p>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 -mt-6 space-y-4">
        
        {/* Performance Snapshot */}
        <Card className="p-4 bg-white rounded-2xl shadow-sm">
          <h2 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">Performance</h2>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-emerald-50 rounded-xl">
              <p className="text-lg font-bold text-emerald-700">{formatLacs(stats.approved_booking_av_lacs)}</p>
              <p className="text-[10px] text-slate-500 mt-1">Approved AV</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-xl">
              <p className="text-lg font-bold text-blue-700">{stats.agreements_done || 0}</p>
              <p className="text-[10px] text-slate-500 mt-1">Agreements</p>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-xl">
              <p className="text-lg font-bold text-purple-700">{stats.registrations_done || 0}</p>
              <p className="text-[10px] text-slate-500 mt-1">Registrations</p>
            </div>
          </div>
        </Card>

        {/* Profile Details Card */}
        <Card className="p-4 bg-white rounded-2xl shadow-sm">
          <h2 className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">Details</h2>
          
          <div className="space-y-4">
            {/* Email */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Mail size={18} className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-400">Email</p>
                <p className="text-sm text-slate-800 truncate">{user.email}</p>
              </div>
            </div>
            
            {/* Manager */}
            {user.reporting_supervisor_name && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <User size={18} className="text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400">Manager</p>
                  <p className="text-sm text-slate-800">{user.reporting_supervisor_name}</p>
                </div>
              </div>
            )}
            
            {/* Designation */}
            {user.designation && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Briefcase size={18} className="text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400">Designation</p>
                  <p className="text-sm text-slate-800">{user.designation}</p>
                </div>
              </div>
            )}
            
            {/* City */}
            {user.city && (
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Building2 size={18} className="text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-400">City</p>
                  <p className="text-sm text-slate-800">{user.city}</p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Bio Card */}
        <Card 
          className="p-4 bg-white rounded-2xl shadow-sm cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setBioModalOpen(true)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-sm font-semibold text-slate-500 mb-2 uppercase tracking-wide">About</h2>
              <p className="text-sm text-slate-600">
                {user.bio || 'Add a short bio about yourself...'}
              </p>
            </div>
            <Edit2 size={16} className="text-slate-400 flex-shrink-0" />
          </div>
        </Card>

        {/* Logout Button */}
        <Button
          variant="outline"
          onClick={handleLogout}
          className="w-full h-12 border-red-200 text-[#5B3DF5] hover:bg-red-50 rounded-xl"
        >
          <LogOut size={18} className="mr-2" />
          Logout
        </Button>
      </div>

      {/* Photo Upload Modal */}
      <Dialog open={photoModalOpen} onOpenChange={setPhotoModalOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Profile Photo</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
              onChange={handlePhotoUpload}
              className="hidden"
            />
            
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 rounded-xl transition-colors"
            >
              <div className="p-2 bg-blue-100 rounded-lg">
                <ImagePlus size={20} className="text-blue-600" />
              </div>
              <span className="text-slate-700 font-medium">Choose from Gallery</span>
            </button>
            
            {user.profile_image_url && (
              <button
                onClick={handleRemovePhoto}
                className="w-full p-4 flex items-center gap-3 hover:bg-red-50 rounded-xl transition-colors"
              >
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 size={20} className="text-[#5B3DF5]" />
                </div>
                <span className="text-[#5B3DF5] font-medium">Remove Photo</span>
              </button>
            )}
            
            <button
              onClick={() => setPhotoModalOpen(false)}
              className="w-full p-4 flex items-center justify-center text-slate-500 hover:bg-slate-50 rounded-xl transition-colors"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bio Edit Modal */}
      <Dialog open={bioModalOpen} onOpenChange={setBioModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Bio</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value.slice(0, 150))}
                placeholder="Write a short bio about yourself..."
                rows={4}
                className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#EF4444] resize-none"
              />
              <p className="text-xs text-slate-400 text-right mt-1">{bio.length}/150</p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setBioModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveBio}
                disabled={savingBio}
                className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
              >
                {savingBio ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
